import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';
import { i18n } from '@/shared/i18n';

interface Props {
  iso: string;
  /**
   * Optional semantic prefix for the tooltip, clarifying which date this is
   * (e.g. "Added to library"). When set, the tooltip reads "<label> · <full
   * date>"; otherwise it shows the full date alone.
   */
  label?: string;
}

/**
 * Date + time stamp shared between Library / Folders asset cards and the
 * picker table. Tooltip exposes the full locale string for users who want
 * day-of-week, year, and seconds.
 *
 * Both formats are keyed on `i18n.language`, not the browser locale: month
 * name, field order and hour cycle all differ per language, and passing
 * `undefined` renders an English month inside a translated frame whenever the
 * two disagree. Delegating to `Intl` is also why there is no hand-rolled
 * 12-hour clock here — Spanish and Portuguese use a 24-hour one.
 */
export function DateTimeCell(props: Props) {
  const date = new Date(props.iso);
  const display = new Intl.DateTimeFormat(i18n.language, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
  const fullDate = date.toLocaleString(i18n.language);
  const fullLabel = props.label ? `${props.label} · ${fullDate}` : fullDate;
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-muted-foreground">{display}</span>
        </TooltipTrigger>
        <TooltipContent>{fullLabel}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
